import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
	FormGroup,
	Input,
	Label
} from 'reactstrap';

import { messages } from '../../../_shared/i18n/messages';

const propTypes = {

	/**
	 * The input name.
	 */
	name: PropTypes.string.isRequired,

	/**
	 * The input value.
	 */
	value: PropTypes.bool.isRequired,

	/**
	 * The input label text.
	 */
	label: (props, propName, componentName) => {
		// A visible label is required unless the caller supplies an
		// accessible name another way (ariaLabel) — e.g. inline table-cell
		// controls whose column header is the visual label. (prop-types
		// validators can't be invoked directly from a custom validator, so
		// the accepted shapes — string / element / array / object — are
		// checked inline.)
		const value = props[propName];

		if (value == null) {
			return props.ariaLabel
				? null
				: new Error(`The prop \`${propName}\` is required in \`${componentName}\` unless \`ariaLabel\` is provided.`);
		}

		const isValid = typeof value === 'string'
			|| Array.isArray(value)
			|| (typeof value === 'object' && value !== null);

		return isValid
			? null
			: new Error(`Invalid prop \`${propName}\` supplied to \`${componentName}\`: expected a string, element, array or object.`);
	},

	/**
	 * The input placeholder text.
	 */
	placeholder: PropTypes.string,

	/**
	 * True, if editing the input value.
	 */
	isEditing: PropTypes.bool,

	/**
	 * True, if the input value is invalid.
	 */
	isInvalid: PropTypes.bool,

	/**
	 * True, if the label text should be displayed inline with the input.
	 */
	isLabelInline: PropTypes.bool,

	/**
	 * True, if the input value is disabled.
	 */
	disabled: PropTypes.bool,

	/**
	 * The input aria-label (if any) — an accessible name for label-less
	 * inline usages such as table-cell toggles.
	 */
	ariaLabel: PropTypes.string,

	/**
	 * Callback function to handle changes to the input value.
	 */
	onChange: PropTypes.func,

};

const CheckboxInput = (props) => {

	const {
		name,
		value,
		label,
		placeholder,
		isLabelInline,
		isEditing,
		isInvalid,
		disabled,
		ariaLabel,
		onChange,
	} = props;

	const intl = useIntl();

	if (!isEditing) {
		return (
			<div className="tc-material-input-display tc-material-input-display-checkbox">
				{((value === true) || (value === false)) &&
					<FormGroup check>
						<Input
							name={`option-display-${name}`}
							type="checkbox"
							checked={value}
							disabled />
						{' '}
						<Label
							for={`option-display-${name}`}
							check>
							{value === true
								? intl.formatMessage(messages.TC_COMPONENTS_FORM_INPUT_CHECKBOX_DISPLAY_TEXT_TRUE)
								: intl.formatMessage(messages.TC_COMPONENTS_FORM_INPUT_CHECKBOX_DISPLAY_TEXT_FALSE)
							}
						</Label>
					</FormGroup>
				}
				{((value === null || value === undefined)) &&
					<span className="tc-material-input-display-empty-value">
						<FormattedMessage {...messages.TC_COMPONENTS_FORM_INPUT_TEXT_EMPTY_VALUE} />
					</span>
				}
			</div>
		);
	}

	return (
		<FormGroup
			className={`tc-material-input-checkbox ${isInvalid ? 'tc-material-input-invalid' : ''}`}
			check>
			<Input
				id={`tc-checkbox-${name}-input`}
				name={name}
				type="checkbox"
				aria-label={ariaLabel}
				checked={value}
				invalid={isInvalid}
				disabled={disabled}
				onChange={(event) => onChange(event.target.checked)} />
			{' '}
			<Label
				for={`tc-checkbox-${name}-input`}
				check>
				{isLabelInline ? label : placeholder}
			</Label>
		</FormGroup>
	);
};

CheckboxInput.propTypes = propTypes;

export default CheckboxInput;
